import { FC, useCallback, useEffect, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Video, { Room } from 'twilio-video';
import { isMobile } from 'react-device-detect';
import { isEmpty, isUndefined } from 'lodash';
import { CurrentUserReducer, RootReducer, StreamingReducer } from '../../../../models/Redux';
import { SET_LOCAL_STREAMING_STATE } from '../../../../store/actions/Streaming.actions';
import useVideoContext from '../../hooks/useVideoContext/useVideoContext';

const StreamingManager: FC = () => {
    const dispatch = useDispatch();
    const connectingStream = useRef<boolean>(false);

    const {
        user: { user_ide: userIde },
    } = useSelector<RootReducer, CurrentUserReducer>(state => state.currentUser);

    const { localTracks, removeLocalAudioTrack, removeLocalVideoTrack } = useVideoContext();

    const {
        eventUuid,
        eventData: {
            user: { user_ide: influencerUserIde },
        },
        streamingState: { token },
        streamingFlags: { started },
        localStreamingState,
    } = useSelector<RootReducer, StreamingReducer>(state => state.streaming);

    const isEventInfluencer = useMemo(() => influencerUserIde === userIde, [influencerUserIde, userIde]);

    const setRoom = useCallback(
        (localRoom: Room | null) => {
            dispatch({
                type: SET_LOCAL_STREAMING_STATE,
                payload: {
                    ...localStreamingState,
                    room: localRoom,
                },
            });
        },
        [dispatch, localStreamingState],
    );

    const connectStreaming = useCallback(async () => {
        if (!isEmpty(token) && !isUndefined(token) && !localStreamingState.room) {
            connectingStream.current = true;
            const renderDimensions = {
                high: { height: 1080, width: 1920 },
                standard: { height: 1080, width: 1920 },
                low: { height: 1080, width: 1920 },
            };

            const mobileRenderDimensions = {
                high: { height: 1920, width: 1080 },
                standard: { height: 1920, width: 1080 },
                low: { height: 1920, width: 1080 },
            };
            /*
                        const influencerConnectionProperties = {
                            dominantSpeaker: true,
                            video: true,
                            audio: true,
                            preferredVideoCodecs: [{ codec: 'VP8', simulcast: true }],
                            bandwidthProfile: {
                                video: {
                                    mode: 'presentation',
                                    maxTracks: 3,
                                    dominantSpeakerPriority: 'standard',
                                    renderDimensions: isMobile ? mobileRenderDimensions : renderDimensions,
                                },
                            },
                        };

                        const viewerConnectionProperties = {
                            dominantSpeaker: true,
                            video: hasTurn,
                            bandwidthProfile: {
                                video: {
                                    mode: 'presentation',
                                    maxTracks: 3,
                                    dominantSpeakerPriority: 'standard',
                                    renderDimensions: isMobile ? mobileRenderDimensions : renderDimensions,
                                },
                            },
                            audio: hasTurn || openMic,
                        };

                        const options = isEventInfluencer ? influencerConnectionProperties : viewerConnectionProperties;

                        let localTracks;



                        try {
                            localTracks = await Video.createLocalTracks(options);
                        } catch (e) {
                            enqueueSnackbar(
                                "Unable to access your camera or microphone due to permissions with your browser. You won't be able to talk or show your video but you can still watch the event.",
                                { variant: 'error', autoHideDuration: 15000 },
                            );
                            console.error('Error creating local tracks', e);
                        }


                         */

            if (!isEventInfluencer) {
                removeLocalAudioTrack();
                removeLocalVideoTrack();
            }

            Video.connect(token, {
                name: eventUuid,
                audio: false,
                dominantSpeaker: true,
                video: false,
                tracks: isEventInfluencer ? localTracks : [],
                automaticSubscription: true,
                preferredVideoCodecs: [{ codec: 'VP8', simulcast: true }],
                bandwidthProfile: {
                    video: {
                        mode: 'presentation',
                        maxTracks: 3,
                        dominantSpeakerPriority: 'standard',
                        renderDimensions: isMobile ? mobileRenderDimensions : renderDimensions,
                    },
                },
            })
                .then(newRoom => {
                    const disconnect = () => newRoom.disconnect();

                    // This app can add up to 13 'participantDisconnected' listeners to the room object, which can trigger
                    // a warning from the EventEmitter object. Here we increase the max listeners to suppress the warning.
                    newRoom.setMaxListeners(49);

                    newRoom.once('disconnected', () => {
                        // Reset the room only after all other `disconnected` listeners have been called.
                        setTimeout(() => setRoom(null));
                        window.removeEventListener('beforeunload', disconnect);

                        if (isMobile) {
                            window.removeEventListener('pagehide', disconnect);
                        }
                    });

                    setRoom(newRoom);
                })
                .catch(error => {
                    console.error('Error connecting with Twilio room:', error);
                })
                .then(() => {
                    connectingStream.current = false;
                });
        }
    }, [
        token,
        localStreamingState,
        isEventInfluencer,
        eventUuid,
        localTracks,
        removeLocalAudioTrack,
        removeLocalVideoTrack,
        setRoom,
    ]);

    useEffect(() => {
        if (started && token && !connectingStream.current) {
            connectStreaming();
        }
    }, [connectStreaming, started, token]);

    return null;
};

export default StreamingManager;
